import {
  useRef, useCallback, useEffect, useState,
  useContext,
} from 'react';
import { isEqual } from 'lodash';
import { makeStyles } from 'tss-react/mui';
import { getLocation } from '@sda/utils/location';
import { getBoundsFromMapResult, parseReverseGeocodeResults } from '@sda/utils/location/geocode';
import {
  Cluster, Dot, Map, Pin,
} from '@client/components/common/Map';
import { useFormValues, useSetFieldValue } from '@client/hooks/formik.hooks';
import { useDispatch, useSelector } from '@client/hooks/redux.hooks';
import { getCurrentPosition } from '@client/selectors/helpers/session';
import { useHasHover, useIsMobile } from '@client/hooks/dom.hooks';
import { zIndex } from '@client/components/common/Map/Map/common/constants';
import { startFetchUserLocation } from '@client/slices/helpers/session.reducer';
import { setBounds, setZoom } from '@client/slices/features/map.reducer';
import {
  getMapExtraHits, getFilteredMapHits, getMapHoverId, getMapZoom, MAX_ZOOM, getHoveredClusterId,
} from '@client/selectors/features/map';
import { PinTypes } from '../interfaces';
import { SetAnchorContext } from './SetAnchorContext';
import type { Location } from '@sda/utils/location/interfaces';
import type { ChangeEventProps, LoadedProps } from '@client/components/common/Map/Map/interfaces/GoogleMapReact';

const useStyles = makeStyles({ name: 'DynamicMap' })((theme) => ({
  mapContainer: {
    touchAction: 'pan-x pan-y',

    [theme.breakpoints.down('md')]: {
      position: 'absolute',
      top: 0,
    },
  },
}));

export const DynamicMap = () => {
  const { classes } = useStyles();
  const setAnchor = useContext(SetAnchorContext);
  const isMobile = useIsMobile();
  const hasHover = useHasHover();

  const dispatch = useDispatch();

  const [center, setCenter] = useState<Location>({ lat: 0, lng: 0 });

  const zoom = useSelector(getMapZoom);
  const location = useSelector(getCurrentPosition);
  const mapRef = useRef<LoadedProps['map']>(null);
  const setFieldValue = useSetFieldValue();

  const handleMapChange = useCallback((change: ChangeEventProps) => {
    const newLocation = getLocation({ lat: change.center[1], lng: change.center[0] });
    dispatch(setZoom(change.zoom));
    setAnchor({ anchorId: null, anchorEl: null });

    const newBounds = getBoundsFromMapResult(change.bounds);
    dispatch(setBounds(newBounds));

    if (!isEqual(newLocation, center)) {
      setCenter(newLocation);
      void setFieldValue('latLng', newLocation);

      if (mapRef.current) {
        const geocoder = new google.maps.Geocoder();

        void geocoder.geocode({ location: newLocation }, (response) => {
          const results = parseReverseGeocodeResults(response);

          if (results.city) {
            void setFieldValue('compiledAddress', `${results.city}, ${results.regionCode}, ${results.countryCode}`);
            void setFieldValue('googlePlaceId', results.googlePlaceId);
          } else {
            void setFieldValue('compiledAddress', null);
            void setFieldValue('googlePlaceId', null);
          }
        });
      }
    }
  }, [setFieldValue, dispatch, center, setAnchor]);

  useEffect(() => {
    if (!mapRef.current) {
      dispatch(startFetchUserLocation());
    }
  }, [dispatch]);

  const handleMapLoaded = useCallback((props: LoadedProps) => {
    mapRef.current = props.map;
  }, []);

  const [clusters, supercluster] = useSelector(getFilteredMapHits);
  const extraHits = useSelector(getMapExtraHits);
  const hoverId = useSelector(getMapHoverId);
  const hoveredClusterId = useSelector(getHoveredClusterId);

  const pinTypes = useFormValues<PinTypes[]>('pinTypes');

  const handleClusterClick = useCallback((latLng: Location, clusterId: number) => {
    const expansionZoom = Math.min(
      supercluster.getClusterExpansionZoom(clusterId),
      MAX_ZOOM,
    );
    mapRef.current.setZoom(expansionZoom);

    mapRef.current.setCenter(latLng);
    mapRef.current.setZoom(zoom + 1);
  }, [supercluster, zoom]);

  const handlePinClick = useCallback((e, pinId: string) => {
    setAnchor({ anchorEl: e.currentTarget, anchorId: pinId });
  }, [setAnchor]);

  if (!location) {
    return null;
  }

  return (
    <Map
      location={{ lat: location.latitude, lng: location.longitude }}
      zoom={zoom}
      height="100%"
      onChange={handleMapChange}
      onGoogleApiLoaded={handleMapLoaded}
      className={classes.mapContainer}
      MapOptions={{
        maxZoom: MAX_ZOOM,
        minZoom: 4,
        zoomControl: true,
        scaleControl: true,
        gestureHandling: (isMobile || !hasHover) ? 'greedy' : 'cooperative',
        restriction: {
          latLngBounds: {
            north: 85,
            south: -85,
            west: -180,
            east: 180,
          },
        },
      }}
    >
      {pinTypes.includes(PinTypes.PROFILES) && (
        <>
          {extraHits.map((hit) => {
            if (hit._geoloc?.lat) {
              return (
                <Dot
                  key={hit.objectID}
                  lat={hit._geoloc.lat}
                  lng={hit._geoloc.lng}
                  zIndex={zIndex.dot}
                />
              );
            }

            return null;
          })}
        </>
      )}

      {pinTypes.includes(PinTypes.SHOWCALLS) && (
        <>
          {clusters.map((cluster) => {
            const [longitude, latitude] = cluster.geometry.coordinates;
            const {
              cluster: isCluster,
              point_count: pointCount,
            } = cluster.properties;

            if (isCluster) {
              return (
                <Cluster
                  key={`cluster-${cluster.id}`}
                  lat={latitude}
                  lng={longitude}
                  numPoints={pointCount}
                  onClick={() => handleClusterClick({ lat: latitude, lng: longitude }, cluster.id as number)}
                  isActive={hoveredClusterId === cluster.id}
                  zIndex={zIndex.cluster}
                />
              );
            }

            return (
              <Pin
                key={cluster.properties.objectID}
                lat={latitude}
                lng={longitude}
                isActive={cluster.properties.objectID === hoverId || cluster.properties.profileId === hoverId}
                onClick={(e) => handlePinClick(e, cluster.properties.objectID)}
                zIndex={zIndex.marker}
              />
            );
          })}
        </>
      )}
    </Map>
  );
};
